import { ValueDepreciationConstants, type IValueDepreciationConstants } from '../constants/value-depreciation.constants';
import type { IValueDepreciationParameters } from '../models/parameters.model';
import type { IValueDepreciationResult } from '../models/result.model';
import { ValueDepreciationScenario } from '../models/value-depreciation-scenario.enum';

export class ValueDepreciationCalculator {
  private static findValueDepreciationConstant(constants: IValueDepreciationConstants, scenario: ValueDepreciationScenario, year: number): number {
    if (year <= 0) throw new Error('Year must be greater than 0');

    if (scenario === ValueDepreciationScenario.Low) {
      if (year === 1) return constants.lowYear1;
      if (year >= 2 && year <= 5) return constants.lowYear2to5;
      if (year >= 6 && year <= 20) return constants.lowYear6to20;
      if (year >= 21 && year <= 30) return constants.lowYear21to30;
    }
    if (scenario === ValueDepreciationScenario.Normal) {
      if (year === 1) return constants.mediumYear1;
      if (year >= 2 && year <= 5) return constants.mediumYear2to5;
      if (year >= 6 && year <= 20) return constants.mediumYear6to20;
      if (year >= 21 && year <= 30) return constants.mediumYear21to30;
    }
    if (scenario === ValueDepreciationScenario.High) {
      if (year === 1) return constants.highYear1;
      if (year >= 2 && year <= 5) return constants.highYear2to5;
      if (year >= 6 && year <= 20) return constants.highYear6to20;
      if (year >= 21 && year <= 30) return constants.highYear21to30;
    }
    return 0;
  }

  public static calculateVariableDepreciationCostPerMonth(params: IValueDepreciationParameters, variableDepreciationPercentage: number | undefined): number {
    const depreciationFactor = variableDepreciationPercentage ? variableDepreciationPercentage / 100 : 1;
    return Math.round(
      (depreciationFactor *
        ValueDepreciationCalculator.calculateCost(
          {
            newCarPriceSek: params.newCarPriceSek,
            carAgeInYears: params.carAgeInYears,
            valueDepreciationScenario: params.valueDepreciationScenario,
          },
          ValueDepreciationConstants
        ).costInSek) /
        12
    );
  }

  public static calculateCost(params: IValueDepreciationParameters, constants: IValueDepreciationConstants): IValueDepreciationResult {
    if (params.newCarPriceSek === undefined) {
      return { costInSek: Number.NaN };
    }

    let accumulatedValueDepreciation = 0;
    for (let i = 1; i <= params.carAgeInYears; i += 1) {
      accumulatedValueDepreciation += (params.newCarPriceSek - accumulatedValueDepreciation) * this.findValueDepreciationConstant(constants, params.valueDepreciationScenario, i);
    }

    const nextYearValueDepreciation =
      (params.newCarPriceSek - accumulatedValueDepreciation) * this.findValueDepreciationConstant(constants, params.valueDepreciationScenario, params.carAgeInYears + 1);

    return {
      costInSek: Math.round(nextYearValueDepreciation),
    };
  }
}

